import React, { useState, useContext, useEffect, useCallback } from 'react'
import { Paper, Grid, ButtonBase, Button } from '@material-ui/core'
import { Apple } from '@material-ui/icons'
import { UserApi } from '../../api/user.api.js'
import WalletPayment from './WalletPayment/WalletPayment.js'
import { GooglePayIcon, DebitIcon, BankIcon } from '../Core/Icons/PaymentIcons.js'
import { useStripe, useElements } from '@stripe/react-stripe-js'
import ErrorOutline from '@material-ui/icons/ErrorOutline'
import 'react-credit-cards/es/styles-compiled.css'
import './CreditCard.scss'
import { BuyingInsuranceContextStore, methodsToFlags } from '../../context/BuyingInsuranceContext'
import { PAYMENT_METHODS, WALLETS, PAYMENT_FEE, billingFlowType, flowType } from '../../Constants'
import { SessionContextStore } from '../../context/SessionContext'
import CheckoutForm from './Debit/CheckoutForm.js'
import { RectangleFrame } from '../Core/Frames/RectangleFrame.js'
import { SkyInformationPopover } from '../Core/Popover/SkyPopover.js'
import { makeStyles } from '@material-ui/styles'
import { isInFlow } from '../../services/utils'
import { useLocation } from 'react-router-dom'
import { SessionService } from '../../services/SessionService.js'
import BankPayment from './BankPayment/BankPayment.js'
import { reportGTM } from '../Core/Utilities/GTagManger'
import { IS_ACH_ACTIVE } from '../../Constants'
import { Skeleton } from '@material-ui/lab'
import { StatesContextStore } from '../../context/StatesContext.js'

const usePaperStyle = makeStyles(theme => ({
  root: {
    '&.checked': {
      border: '1px solid #5743af',
      backgroundColor: '#dfd4f3',
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
}))

const useRippleStyle = makeStyles(theme => ({
  root: {
    '&': {
      width: '100%',
      display: 'block',
    },
  },
}))

const PaymentIcon = ({ paymentMethod }) => {
  switch (paymentMethod) {
    case 'Credit/Debit':
      return <DebitIcon />
    case 'Apple pay':
      return <Apple />
    case 'Google pay':
      return <GooglePayIcon />
    case 'Bank':
      return <BankIcon />
    default:
      return <GooglePayIcon />
  }
}

const CreditCardMethods = ({ onPurchase }) => {
  const location = useLocation()
  const paperClasses = usePaperStyle()
  const rippleClasses = useRippleStyle()
  const stripe = useStripe()
  const elements = useElements()
  /*const {
    vehiclesDetailes,
    pricebreakdown,
    setPaymentMethod,
    paymentMethod,
    clientSecret,
    getClientSecret,
    isBankLinked,
    setIsBankLinked
  } = useContext(BuyingInsuranceContextStore);*/
  const { newPaymentFlow, user, creditCardAlert } = useContext(SessionContextStore)
  const [isUpdateCard, setIsUpdateCard] = useState(false)
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [availableMethods, setAvailableMethods] = useState({ credit: true, bank: false })
  const [isWalletReady, setIsWalletReady] = useState(null)
  const [shouldCollectMandate, setShouldCollectMandate] = useState(false)
  const [loadingBankPay, setLoadingBankPay] = useState(false)
  const offersContext = useContext(BuyingInsuranceContextStore)
  const {
    vehiclesDetailes,
    pricebreakdown,
    setPaymentMethod,
    paymentMethod,
    clientSecret,
    getClientSecret,
    isBankLinked,
    setIsBankLinked,
    billingFlowType,
  } = offersContext
  const statesContext = useContext(StatesContextStore)

  const renderMethod = () => {
    switch (paymentMethod) {
      case 'Credit/Debit':
        return <CheckoutForm stripe={stripe} elements={elements} onPurchase={onPurchase} />
      case 'Apple pay':
      case 'Google pay':
        return <WalletPayment paymentRequest={paymentRequest} onPurchase={onPurchase} />
      case 'Bank':
        return (
          <BankPayment
            onPurchase={onPurchase}
            clientSecret={clientSecret}
            stripe={stripe}
            loading={loadingBankPay}
            shouldCollectMandate={shouldCollectMandate}
            setShouldCollectMandate={setShouldCollectMandate}
          />
        )
      default:
        return <CheckoutForm onPurchase={onPurchase} />
    }
  }

  useEffect(() => {
    if(location.pathname.startsWith('/account/credit-card')){
      setIsUpdateCard(true)
    }
  }, [])

  const initiateBankPay = async () => {
    setLoadingBankPay(true)
    const { setupIntent, error } = await stripe.collectBankAccountForSetup({
      clientSecret: clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        payment_method_data: {
          billing_details: {
            name: `${user.firstName} ${user.lastName}`,
            email: user.email,
          },
        },
      },
      expand: ['payment_method'],
    })

    setLoadingBankPay(false)
    if (error) return

    switch (setupIntent.status) {
      case 'requires_payment_method':
        break
      case 'requires_confirmation':
        setIsBankLinked(true)
        setShouldCollectMandate(true)
        break
    }
  }

  const methodToFunction = method => {
    if (!paymentRequest && !initiateBankPay) return
    switch (method) {
      case 'Bank':
        reportGTM('credit-card', 'Purchase', { event: 'ach_payment_chosen' })
        initiateBankPay()
        break
      case 'Apple pay':
      case 'Google pay':
        paymentRequest.show()
        break
      default:
        break
    }
  }

  const handleChangePaymentMethod = (event, newValue) => {
    setPaymentMethod(newValue)
    methodToFunction(newValue)
  }

  useEffect(() => {
    if (isWalletReady && !availableMethods[isWalletReady]) setAvailableMethods({ ...availableMethods, [isWalletReady]: true })
    if (IS_ACH_ACTIVE && clientSecret && !availableMethods['bank']) setAvailableMethods({ ...availableMethods, bank: true })
  }, [isWalletReady, clientSecret])

  useEffect(() => {
    if (!stripe || !elements) return
    if (SessionService.isBroker()) return

    if (!isInFlow(location.pathname)) return
    let additionalFees = pricebreakdown.additionalFees
    const minimumEarnedPremium = pricebreakdown.minimumEarnedPremium
    const paymentFee = statesContext.no_initial_payment_fee.includes(user.state) ? 0 : PAYMENT_FEE
    let total = paymentFee + pricebreakdown.fixedPrice + additionalFees + minimumEarnedPremium
    total += newPaymentFlow
      ? billingFlowType === flowType.ESTIMATED_MILEAGE
        ? pricebreakdown.totalEstimatedMileageCharge
        : pricebreakdown.totalBaseMilesCharge
      : 0

    const pr = stripe.paymentRequest({
      country: 'US',
      currency: 'usd',
      total: {
        label: 'Total',
        amount: parseInt(total * 100),
      },
      requestPayerEmail: true,
    })

    pr.canMakePayment().then(result => {
      if (!result) return
      setPaymentRequest(pr)
      setIsWalletReady(result.googlePay ? 'googlePay' : result.applePay ? 'applePay' : null)
    })
  }, [stripe, elements])

  useEffect(() => {
    if (!paymentRequest) return
    paymentRequest.on('paymentmethod', async e => {
      let method = 'Google/Apple'
      switch (e.walletName) {
        case 'googlePay':
          method = 'Google-Pay'
          break
        case 'applePay':
          method = 'Apple-Pay'
          break
      }
      let response = await UserApi.attachPaymentMethod(e.paymentMethod.id, false, method)
      if (response.ok) {
        e.complete('success')
        await onPurchase(offersContext)
        return
      }
      e.complete('fail')
    })
  }, [paymentRequest])

  useEffect(() => {
    if (IS_ACH_ACTIVE && !clientSecret) {
      getClientSecret()
    }
  }, [])

  return (
    <>
      <Grid container>
        <Grid container item xs={12} sm={7} spacing={1}>
          {Object.keys(PAYMENT_METHODS).map(method => {
            const hideMethod = !availableMethods || !availableMethods[methodsToFlags[method]]
            return (
              <Grid item xs={4} sm={3} key={method} hidden={hideMethod} onClick={e => handleChangePaymentMethod(e, method)}>
                <ButtonBase aria-label={methodsToFlags[method]} data-testid={methodsToFlags[method]} className={rippleClasses.root}>
                  <Paper
                    className={`${method === paymentMethod ? 'checked' : ''} ${paperClasses.root} padding-top-10 padding-bottom-10`}
                    elevation={0}
                    variant="outlined"
                    key={method}
                  >
                    <Grid container justifyContent="center">
                      <PaymentIcon paymentMethod={method} />
                    </Grid>
                    <Grid container justifyContent="center" className="margin-top-6">
                      <span className="gray bold text-small">{PAYMENT_METHODS[method]}</span>
                    </Grid>
                  </Paper>
                </ButtonBase>
              </Grid>
            )
          })}
        </Grid>
        <Grid item xs={12}>
          {renderMethod()}
        </Grid>
        {creditCardAlert && (
          <Grid className="square-frame-design">
            <span className="text-small">
              This credit card is about to expire in less than 90 days. Consider adding a different card or click Purchase to continue{' '}
            </span>
          </Grid>
        )}
        {newPaymentFlow && !isUpdateCard && (
          <Grid>
            <Grid container alignItems="center" wrap="nowrap">
              <ErrorOutline className="purple margin-bottom-6" />
              <span className="text-small margin-left-3">
                {vehiclesDetailes.length < 2 ? (
                  <span>
                    Initial per mile charge will serve as credit for{' '}
                    <b>
                      {billingFlowType === flowType.ESTIMATED_MILEAGE
                        ? parseInt(pricebreakdown.totalEstimatedMileage)
                        : parseInt(pricebreakdown.totalBaseMiles)}{' '}
                      miles{' '}
                    </b>
                    under the policy term{' '}
                  </span>
                ) : (
                  <>
                    <span className="margin-top-basic">Initial per mile charge will serve as credit for the policy term for: </span>
                  </>
                )}
              </span>
            </Grid>
            {vehiclesDetailes.length >= 2 && (
              <span>
                {vehiclesDetailes.map(vehicle => (
                  <div className="text-small margin-left-16 margin-top-3">
                    <b>
                      {billingFlowType === flowType.ESTIMATED_MILEAGE ? vehicle.estimatedBillingPeriodMileage : vehicle.defaultMonthlyMiles}{' '}
                      miles
                    </b>{' '}
                    on the {vehicle.year} {vehicle.make} {vehicle.model}
                  </div>
                ))}
              </span>
            )}
          </Grid>
        )}
        {statesContext.no_initial_payment_fee.includes(user.state) && (
          <Grid>
            <Grid container alignItems="center" wrap="nowrap">
              <ErrorOutline className="purple margin-bottom-6" />
              <span className="text-small margin-left-3">
                <span>A $2 installment fee will be charged from your 2nd policy payment onwards</span>
              </span>
            </Grid>
          </Grid>
        )}
        {!!pricebreakdown.addOnCoveragePrices && !!pricebreakdown.addOnCoveragePrices.find(x=>x.coverageName === 'roadside_assistance' && x.isIncluded === true) && (
          <Grid>
            <Grid container alignItems="center" wrap="nowrap">
              <ErrorOutline className="purple margin-bottom-6" />
              <span className="text-small margin-left-3">
                <span>Please be aware that a $50 portion of the charge for the roadside assistance coverage is fully earned and non-refundable.</span>
              </span>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default CreditCardMethods
